import {
	ApolloClient,
	ApolloLink,
	HttpLink,
	InMemoryCache,
	type InMemoryCacheConfig,
	type TypePolicy,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import * as Sentry from "@sentry/react";
import { SentryLink } from "apollo-link-sentry";
import { type AuthOptions, createAuthLink } from "aws-appsync-auth-link";
import { AUTH_TYPE } from "aws-appsync-auth-link/lib/auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import { useEffect, useMemo, useState } from "react";
import { Config } from "services/configuration/eatSmartConfig";
import { handleSendApolloErrorToSentry } from "services/external/sentry";
import { getFridgeId } from "./browser/localStorage";
import { networkTrace, shouldTraceNetworkDebug } from "./debug/networkTrace";
import { resolveRetryAttempt } from "./retryConnectionPolicy";

// Per-frame snapshot value-objects carried by FRIDGE_SUBSCRIPTION (and the one-shot queries). Each
// has an `id`, so Apollo normalizes it by default — but `orders.last`/`refills.last` only ever point at
// the newest one and physical stock (`Dish`) cycles, so every transaction leaves the previous
// normalized entity orphaned in the cache. The kiosk runs a full day between Hexnode reloads, so those
// orphans accumulate. Nothing reads them by cache id (all UI reads funnel through Redux via
// setInitialDishTypes), so `keyFields: false` stores them inline under their bounded parents
// (Fridge*/DishType*) and overwrites them in place each frame — bounding the cache with no runtime
// sweep. Both the query typenames and their `*Subscription` twins are covered.
const EPHEMERAL_TRANSACTION_TYPENAMES = [
	"Order",
	"OrderSubscription",
	"Refill",
	"RefillSubscription",
	"User",
	"UserSubscription",
	"Dish",
	"DishSubscription",
];

const embeddedEphemeralTypePolicies = Object.fromEntries(
	EPHEMERAL_TRANSACTION_TYPENAMES.map((typename): [string, TypePolicy] => [typename, { keyFields: false }]),
);

export const CACHE_SHAPE: InMemoryCacheConfig = {
	typePolicies: {
		...embeddedEphemeralTypePolicies,
		Fridge: {
			fields: {
				dishes: {
					merge: true,
				},
			},
		},
		DishType: {
			fields: {
				dishes: {
					merge: false,
				},
			},
		},
		DishTypeSubscription: {
			fields: {
				dishes: {
					merge: false,
				},
			},
		},
	},
};

export const useFridgeApolloClient = () => {
	const [isConnected, setIsConnected] = useState(true);
	const [isSubscriptionBroken, setIsSubscriptionBroken] = useState(false);

	/* One client per app lifetime: recreating it discards the cache and re-handshakes
	the AppSync WS, restarting every Apollo hook in the tree (this was the
	handleSetInitialData refire bug). The useState setters captured below are
	identity-stable, and Config is static module config, so the memo runs once. */
	const client = useMemo(() => {
		const { aws, connectionSettings } = Config;
		const { appsyncUrl: url, region } = aws;
		const { connectionTimeout } = connectionSettings;
		const auth: AuthOptions = {
			type: AUTH_TYPE.AWS_LAMBDA,
			token: aws.apiKey,
		};

		const errorLink = onError((error) => {
			handleSendApolloErrorToSentry(error);
		});

		// TEMP network diagnostic: log each GraphQL operation's send/result (the subscription's result
		// handler fires per frame, giving per-frame timing/health) into the on-screen NetworkDebugOverlay.
		// No-op when ?networkDebug is off. Remove with networkTrace.ts (see its header).
		const debugLink = new ApolloLink((operation, forward) => {
			if (!shouldTraceNetworkDebug()) {
				return forward(operation);
			}
			const name = operation.operationName || "anonymous";
			const start = Date.now();
			networkTrace(`[gql] → ${name}`);
			return forward(operation).map((result) => {
				networkTrace(`[gql] ← ${name} ${Date.now() - start}ms err=${result.errors?.length ?? 0}`);
				return result;
			});
		});

		const link = ApolloLink.from([
			debugLink,
			errorLink,
			new RetryLink({
				// Connection-state policy (flip offline on the first subscription error, retry for
				// recovery, backstop at the attempt limit) lives in resolveRetryAttempt so it is unit-tested.
				attempts: (count, operation, error) =>
					resolveRetryAttempt(count, operation.operationName, error, {
						onSubscriptionBroken: () => {
							if (shouldTraceNetworkDebug()) {
								networkTrace("[ws] subscription BROKEN");
							}
							setIsSubscriptionBroken(true);
						},
						onDisconnected: () => {
							if (shouldTraceNetworkDebug()) {
								networkTrace("[ws] DISCONNECTED (offline)");
							}
							setIsConnected(false);
						},
					}),
				delay: {
					initial: 500,
					max: 2000,
				},
			}),
			new SentryLink({
				attachBreadcrumbs: {
					includeError: true,
					includeFetchResult: true,
					includeQuery: true,
					includeVariables: true,
				},
			}),
			createAuthLink({ url: url, region: region, auth: auth }),
			createSubscriptionHandshakeLink(
				{
					url: url,
					region: region,
					auth: auth,
					keepAliveTimeoutMs: connectionTimeout,
				},
				new HttpLink({ uri: url }),
			),
		]);

		return new ApolloClient({
			link: link,
			cache: new InMemoryCache({ ...CACHE_SHAPE }),
		});
	}, []);

	useEffect(() => {
		if (isConnected) {
			return;
		}
		// Guard the probe against piling up: on a slow/flaky network a fetch can outlive the 5s
		// interval, so without these a fresh request fires every tick and they accumulate (memory +
		// sockets on the long-lived kiosk WKWebView). isProbing skips a tick while one is still in
		// flight; the per-probe AbortController caps each request so it can't hang forever; disposed
		// tells a cleanup-triggered abort apart from a timeout abort.
		let disposed = false;
		let isProbing = false;
		let pendingController: AbortController | null = null;

		const ping = setInterval(() => {
			if (isProbing) {
				return;
			}
			isProbing = true;
			const controller = new AbortController();
			pendingController = controller;
			// 4s < the 5s interval, so a hung probe is aborted before the next tick and a fresh one runs.
			const timeout = setTimeout(() => controller.abort(), 4000);
			// Bypass the HTTP cache: on the kiosk WKWebView a plain cacheable GET was served from cache
			// while the device was truly offline (status 0 in ~3ms), so the fetch resolved and falsely
			// flipped us back online — hiding the error modal and thrashing reconnect. cache: "no-store"
			// plus a unique URL force a real network round-trip, so an offline device rejects instead.
			fetch(`https://dns.google/?_=${Date.now()}`, {
				mode: "no-cors",
				cache: "no-store",
				signal: controller.signal,
			})
				.then(() => {
					if (disposed) {
						return;
					}
					if (shouldTraceNetworkDebug()) {
						networkTrace("[conn] DNS ping ok → online");
					}
					setIsConnected(true);
				})
				.catch((error: unknown) => {
					// A cleanup abort (we already reconnected, or unmounted) is not an offline signal — skip
					// it. A timeout abort falls through and is reported like any other failed ping, because
					// the device genuinely couldn't reach DNS.
					if (disposed) {
						return;
					}
					if (shouldTraceNetworkDebug()) {
						networkTrace("[conn] DNS ping failed (still offline)");
					}
					Sentry.withScope((scope) => {
						scope.setExtra("fridgeId", getFridgeId());
						Sentry.captureException(
							new Error("Could not ping Google DNS - Tablet is offline", {
								cause: error,
							}),
						);
					});
				})
				.finally(() => {
					clearTimeout(timeout);
					isProbing = false;
					if (pendingController === controller) {
						pendingController = null;
					}
				});
		}, 5000);
		// The cleanup owns the clear: it runs both on unmount and when isConnected flips back to
		// true (the setIsConnected above re-runs this effect), so the interval can never leak. It also
		// aborts any probe still in flight so a slow request can't outlive the offline state.
		return () => {
			disposed = true;
			clearInterval(ping);
			pendingController?.abort();
		};
	}, [isConnected]);

	return {
		client: client,
		isConnected: isConnected,
		setIsConnected: setIsConnected,
		isSubscriptionBroken: isSubscriptionBroken,
		setIsSubscriptionBroken: setIsSubscriptionBroken,
	};
};
